import { app } from './core/App';
import CharacterSpine, { VehicleSkins, VehicleSkinsValues } from './CharacterSpine';
import { BodyTypes } from './level/LevelManager';
import { RoadDetails } from './level/levels/level';
import { LevelUtil } from './level/levels/LevelUtil';
import { BoostPadAnimated } from './level/entities/BoostPadAnimated';
import Obstacle from './level/entities/obstacle/Obstacle';
import { Passenger } from './level/Passenger';
import TaxiUI from './prefabs/TaxiUI';
import { GameDataHandler } from './GameDataHandler';
import { VehicleManager } from './level/VehicleManager';
import { HitEffect, HitEffectValues } from './level/LaneEntityImageBase';
import { CharacterNameValues, CharacterName, Character } from './Character';
// import VehicleStealRadial from './prefabs/VehicleStealRadial';
import ObstacleBoxes from './level/entities/obstacle/ObstacleBoxes';

export const Driver = {
  Civilian: 'civilian',
  NPCTaxi: 'npcTaxi',
  PlayerTaxi: 'playerTaxi'
};
export type DriverKeys = keyof typeof Driver;
export type DriverValues = typeof Driver[DriverKeys];

export interface DriverProfile {
  driver: DriverValues;
  skin: VehicleSkinsValues;
  accelerationPerMS: number;
  decelerationPerMS: number;
  topSpeedPerMS: number;
  topBoostSpeedPerMS: number;
  maxSpeedPerMS: number;
  minLaneChangeSpeed: number;
  boostSpeedDecayRatePerMS: number;
  playerDistanceMultiplierAhead: number;
  playerDistanceMultiplierBehind: number;
  playerMinDistanceAhead: number;
  playerMaxDistanceAhead: number;
  playerMinDistanceBehind: number;
  playerMaxDistanceBehind: number;
  passengerStealSpeedPerMS: number;
  passengerStealDecayRatePerMS: number;
  passengerStealDefence: number;
}

export interface VehicleDetails {
  vehicleLayer: Phaser.GameObjects.Layer;
  gameUILayer: Phaser.GameObjects.Layer;
  startLane: number;
  startIndentX: number;
  endPointX: number;
}

export interface PickedUpPassenger {
  passenger: Passenger;
  rideStartX: number;
  lastScoredX: number;
  time: number;
  farePointsAccrued: number;
  unstealable: number;
}

export interface OngoingPassengerSteal {
  targetVehicle: Vehicle;
  passenger: Passenger;
  stealPointsAccrued: number;
  isInContact: boolean;
}

export class Vehicle extends window.SpinePlugin.SpineContainer {
  public static skinsToSize: Map<VehicleSkinsValues, number> = new Map([
    [VehicleSkins.TaxiTom, 280],
    [VehicleSkins.TaxiJerry, 280],
    [VehicleSkins.TaxiSpike, 280],
    [VehicleSkins.TaxiToots, 280],
    [VehicleSkins.TaxiNeutral, 280],
    [VehicleSkins.CarGreen, 280]
  ]);

  public static events = {
    Jumped: 'jumped',
    SwitchedCharacter: 'switchedCharacter'
  } as const;

  public isLaneChangingUp: boolean;
  public isLaneChangingDown: boolean;

  protected vehicleManager: VehicleManager;
  protected driverProfile: DriverProfile;
  protected roadDetails: RoadDetails;
  protected vehicleDetails: VehicleDetails;
  protected vehicleLayer: Phaser.GameObjects.Layer;
  protected character: CharacterSpine;

  protected vehicleBody: MatterJS.BodyType | undefined;
  protected taxiUI: TaxiUI | undefined;
  // protected stealUI: VehicleStealRadial | undefined;

  protected pointsScored: number;
  protected isLocked: boolean;
  protected isMoving: boolean;
  protected isStuckInTraffic: boolean;
  protected isBlockingTraffic: boolean;
  protected isBlockedByObstacles: boolean;
  protected isChangingLane: boolean;
  protected isAccelerating: boolean;
  protected isOffScreen: boolean;

  protected passengers: PickedUpPassenger[];
  // protected ongoingPassengerSteals: OngoingPassengerSteal[];
  protected currentLane: number;
  protected accelerationPerMS: number;
  protected decelerationPerMS: number;
  protected normalSpeed: number;
  protected boostSpeed: number;
  protected bonusSpeed: number;
  protected topSpeedPerMS: number;
  protected topBoostSpeedPerMS: number;
  protected passengerStealDefence: number;
  protected drag: number;
  protected speedLimitPerMS: number;
  protected maxSpeedPerMS: number;
  protected minSpeedPerMS: number;
  protected boostSpeedDecayRatePerMS: number;
  protected breakDistance: number;
  protected vehicleGap: number;
  // protected stealZone: number;
  protected safeExtraFinishDistance: number;
  protected currentTime: number;

  protected prevMoveSpeedX: number;
  protected frontVehicleCount: number;
  protected frontObstacleCount: number;
  protected backCount: number;
  protected leftLaneCount: number;
  protected rightLaneCount: number;
  protected queuedRandomLaneChange: boolean;
  // protected queueLaneChangeUp: number;
  // protected queueLaneChangeDown: number;
  protected hasChangedLaneOnce: boolean;
  protected isInRange: boolean;
  protected isInRangeWithTarget: boolean;
  protected inRangeOfStealing: Set<Vehicle>;
  protected inRangeOfStealingWithTarget: Set<Vehicle>;
  protected inRangeOfBeingStolen: number;
  protected stealRadius!: Phaser.Geom.Circle;
  protected vehicleBounds!: Phaser.Geom.Rectangle;
  protected sndEngineRef: string;
  // protected debugGraphics: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, vehicleManager: VehicleManager, driverProfile: DriverProfile, roadDetails: RoadDetails, vehicleDetails: VehicleDetails) {
    let [startX, startY] = LevelUtil.getVehicleStartPoint(vehicleDetails.startLane, vehicleDetails.startIndentX, roadDetails);
    super(scene, scene.sys.spine, startX, startY);

    this.isLaneChangingDown = false;
    this.isLaneChangingUp = false;

    this.vehicleManager = vehicleManager;
    this.driverProfile = driverProfile;
    this.roadDetails = roadDetails;
    this.vehicleDetails = vehicleDetails;
    this.vehicleLayer = this.vehicleDetails.vehicleLayer;

    this.character = new CharacterSpine(this.scene, 0, 0, this.driverProfile.skin);
    this.character.addToUpdateList();
    this.add(this.character);
    this.character.refresh();

    this.pointsScored = 0;
    this.isLocked = false;
    this.isMoving = false;
    this.isStuckInTraffic = false;
    this.isBlockingTraffic = false;
    this.isBlockedByObstacles = false;
    this.isChangingLane = false;
    // if (this.IsPlayer) this.isAccelerating = false;
    // else this.isAccelerating = true;
    this.isAccelerating = true;
    this.isOffScreen = false;

    this.passengers = [];
    // this.ongoingPassengerSteals = [];

    this.currentLane = this.vehicleDetails.startLane;
    this.setScale(LevelUtil.getLaneYVehicleScale(this.currentLane, this.roadDetails));
    this.setDepth(this.currentLane);
    this.accelerationPerMS = this.driverProfile.accelerationPerMS;
    this.decelerationPerMS = this.driverProfile.decelerationPerMS;
    this.passengerStealDefence = this.driverProfile.passengerStealDefence;
    this.normalSpeed = 0;
    this.boostSpeed = 0;
    this.bonusSpeed = 0;
    this.topSpeedPerMS = this.driverProfile.topSpeedPerMS;
    this.topBoostSpeedPerMS = this.driverProfile.topBoostSpeedPerMS;
    this.drag = 0;
    this.speedLimitPerMS = this.driverProfile.maxSpeedPerMS;
    this.maxSpeedPerMS = this.driverProfile.maxSpeedPerMS;
    this.minSpeedPerMS = 0.05;
    this.boostSpeedDecayRatePerMS = this.driverProfile.boostSpeedDecayRatePerMS;
    this.breakDistance = 100;
    this.vehicleGap = 40;
    // this.stealZone = 120;
    this.safeExtraFinishDistance = 500;
    this.currentTime = 0;

    this.prevMoveSpeedX = 0;
    this.frontVehicleCount = 0;
    this.frontObstacleCount = 0;
    this.backCount = 0;
    this.leftLaneCount = 0;
    this.rightLaneCount = 0;
    this.queuedRandomLaneChange = false;
    // this.queueLaneChangeUp = 0;
    // this.queueLaneChangeDown = 0;
    this.hasChangedLaneOnce = false;
    this.isInRange = false;
    this.isInRangeWithTarget = false;
    this.inRangeOfStealing = new Set();
    this.inRangeOfStealingWithTarget = new Set();
    this.inRangeOfBeingStolen = 0;
    this.sndEngineRef = '';

    this.injectMatter(this);

    if (this.IsTaxi) {
      this.taxiUI = new TaxiUI(this.scene, this.x, this.y);
      this.taxiUI.setScale(LevelUtil.getLaneYVehicleScale(this.currentLane, this.roadDetails));
      this.taxiUI.bindToVehicle(this);
      this.vehicleDetails.gameUILayer.add(this.taxiUI);

      if (this.IsPlayer) {
        // this.stealUI = new VehicleStealRadial(this.scene, this.x, this.y);
        // this.vehicleDetails.vehicleLayer.add(this.stealUI);

        // this.stealUI.init();
        //this.add(this.stealUI);
      }
    }

    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);

    this.on(Phaser.GameObjects.Events.DESTROY, () => {
      this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
      this.stopEngineSFX();
      console.log("Destroy vehicles.");
    });

    this.scene.events.once(CharacterSpine.events.StopCharacter, this.stopMoving, this);

    // this.debugGraphics = this.scene.add.graphics();
  }

  get IsPlayer(): boolean {
    return this.driverProfile.driver == Driver.PlayerTaxi;
  }

  get IsNPC(): boolean {
    return this.driverProfile.driver == Driver.NPCTaxi;
  }

  get IsTaxi(): boolean {
    return this.driverProfile.driver == Driver.PlayerTaxi || this.driverProfile.driver == Driver.NPCTaxi;
  }

  get IsTraffic(): boolean {
    return this.driverProfile.driver == Driver.Civilian;
  }

  get IsMoving(): boolean {
    return this.isMoving;
  }

  get IsAccelerating(): boolean {
    return this.isAccelerating;
  }

  get HasFinishedRun(): boolean {
    return this.isOffScreen;
  }

  get HasPassengers(): boolean {
    return this.passengers.length > 0;
  }

  get CurrentCharacter(): CharacterNameValues {
    return Character.skinToCharacterLookup[this.character.CurrentSkin] ?? CharacterName.Tom;
  }

  get CurrentSkin(): VehicleSkinsValues {
    return this.character.CurrentSkin;
  }

  get DriverProfile(): DriverProfile {
    return this.driverProfile;
  }

  get RoadDetails(): RoadDetails {
    return this.roadDetails;
  }

  get VehicleDetails(): VehicleDetails {
    return this.vehicleDetails;
  }

  get VehicleBody(): MatterJS.BodyType | undefined {
    return this.vehicleBody;
  }

  get VehicleBounds(): Phaser.Geom.Rectangle {
    return this.vehicleBounds;
  }

  set VehicleBounds(newBounds: Phaser.Geom.Rectangle) {
    this.vehicleBounds = newBounds;
  }

  get TaxiUI(): TaxiUI | undefined {
    return this.taxiUI;
  }

  get StealUI(): undefined {
    return undefined;// return this.stealUI;
  }

  get CurrentLane(): number {
    return this.currentLane;
  }

  get IsChangingLane(): boolean {
    return this.isChangingLane;
  }

  get HasChangedLaneOnce(): boolean {
    return this.hasChangedLaneOnce;
  }

  get IsBlockedLeftOrUp(): boolean {
    return this.leftLaneCount > 0;
  }

  get IsBlockedRightOrDown(): boolean {
    return this.rightLaneCount > 0;
  }

  public hasPassenger(passenger: Passenger): boolean {
    return this.passengers.some((pickedUpPassenger) => pickedUpPassenger.passenger == passenger);
  }

  public setupDriverProfile(driverProfile: DriverProfile) {
    this.driverProfile = driverProfile;

    this.accelerationPerMS = driverProfile.accelerationPerMS;
    this.decelerationPerMS = this.driverProfile.decelerationPerMS;
    this.topSpeedPerMS = driverProfile.topSpeedPerMS;
    this.topBoostSpeedPerMS = driverProfile.topBoostSpeedPerMS;
    this.speedLimitPerMS = driverProfile.maxSpeedPerMS;
    this.maxSpeedPerMS = driverProfile.maxSpeedPerMS;
    this.boostSpeedDecayRatePerMS = driverProfile.boostSpeedDecayRatePerMS;
    this.passengerStealDefence = driverProfile.passengerStealDefence;
  }

  public refreshStealGeometry(vehicle: Vehicle, vehicleBody: MatterJS.BodyType) {
    // Not all vehicles have a stealUI so these numbers represent the visual steal radial.
    let majorAxis = 521 * 1.1;
    let minorAxis = 282 * 0.7;
    let r = minorAxis / 2;
    let diff = majorAxis - minorAxis;
    let tightness = 2.2;

    // Refresh our steal radius.
    // if (this.stealRadius == null) {
    //   this.stealRadius = new Phaser.Geom.Circle(this.x, this.y, r);
    // } else {
    //   this.stealRadius.x = this.x;
    //   this.stealRadius.y = this.y;
    // }

    // Refresh targets bounds.
    if (vehicle.VehicleBounds == null) {
      vehicle.VehicleBounds = new Phaser.Geom.Rectangle(
        vehicleBody.bounds.min.x,
        vehicleBody.bounds.min.y,
        vehicleBody.bounds.max.x - vehicleBody.bounds.min.x,
        vehicleBody.bounds.max.y - vehicleBody.bounds.min.y
      );
    } else {
      vehicle.VehicleBounds.setTo(
        vehicleBody.bounds.min.x,
        vehicleBody.bounds.min.y,
        vehicleBody.bounds.max.x - vehicleBody.bounds.min.x,
        vehicleBody.bounds.max.y - vehicleBody.bounds.min.y
      );
    }

    // Inflate the targets bounds to inverse the ellipse shrink.
    Phaser.Geom.Rectangle.Inflate(vehicle.VehicleBounds, diff / tightness, 0);
  }

  public resetPosition(x: number, y: number) {
    this.scene.matter.body.setPosition(this.body as MatterJS.BodyType, { x: x, y: y });
    this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: 0, y: 0 });
  }

  public shiftPosition(x: number, y: number) {
    console.log(`Shifting player from ${this.x}, ${this.y} to ${x}, ${y}`);
    this.scene.matter.body.setPosition(this.body as MatterJS.BodyType, { x: x, y: y });
  }

  public startMoving() {
    console.log('Moving');
    this.isMoving = true;
    this.character.startMoving();
    this.isLocked = false;
    this.setEngineSFX('SndEngine1');
  }

  public stopMoving() {
    console.log('Stopping');
    this.isMoving = false;
    this.character.stopMoving();
    this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: 0, y: 0 });
    this.isLocked = true;
    this.stopEngineSFX();
    // this.scene.matter.body.setStatic(this.body as MatterJS.BodyType, true);
  }

  public freeze() {
    this.isMoving = false;
    this.character.stopMoving();
    this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: 0, y: 0 });
    this.scene.matter.body.setStatic(this.body as MatterJS.BodyType, true);
    this.isLocked = true;
  }

  public runFinished() {
    this.startMoving();
    this.accelerate();
    this.isLocked = true;
    this.stopEngineSFX();
  }

  public cleanUp() {
    this.scene.matter.body.setPosition(this.body as MatterJS.BodyType, { x: this.x + this.safeExtraFinishDistance + 1000, y: this.y });
    this.stopEngineSFX();
  }

  public teleportToPositionInLane(laneNumber: number, x: number) {
    this.currentLane = laneNumber;
    let targetY = LevelUtil.getVehicleLaneYFromTopToMid(laneNumber, this.roadDetails);
    this.setPosition(x, targetY);
    this.setScale(LevelUtil.getLaneYVehicleScale(this.currentLane, this.roadDetails));
    this.setDepth(laneNumber);
  }

  public switchSkin(skin: VehicleSkinsValues) {
    this.character.switchSkin(skin);
    this.driverProfile.skin = skin;
  }

  public accelerate() {
    if (this.isLocked || this.IsPlayer) return;

    this.isAccelerating = true;
  }

  public decelerate() {
    if (this.isLocked || this.IsPlayer) return;

    this.isAccelerating = false;
  }

  public changeLaneUp(): boolean {
    if (
      this.currentLane <= 0 ||
      this.leftLaneCount > 0 ||
      (this.prevMoveSpeedX < this.driverProfile.minLaneChangeSpeed && !this.isStuckInTraffic && !this.isBlockedByObstacles) ||
      this.isLocked
    )
      return false;
    if (this.isChangingLane) {
      // if (!this.IsPlayer) {
      //   this.queueLaneChangeUp++;
      // }
      return false;
    }

    this.isChangingLane = true;
    this.hasChangedLaneOnce = true;

    this.currentLane--;
    this.setDepth(this.currentLane);

    console.log(`${this.CurrentSkin}: Up to lane ${this.currentLane}`);

    this.scene.add.tween({
      targets: this,
      // y: `-= ${LevelUtil.getLaneYFromTopToTop(this.currentLane + 1, this.roadDetails)}`,
      y: `-= ${LevelUtil.getLaneYChangeMidToMid(this.currentLane, this.currentLane + 1, this.roadDetails)}`,
      scale: `-= ${this.roadDetails.laneVehicleScale}`,
      duration: 250,
      ease: Phaser.Math.Easing.Linear,
      onComplete: () => {
        this.isChangingLane = false;
        this.postLaneChange();
      },
      onCompleteScope: this
    });

    this.scene.add.tween({
      targets: this.taxiUI,
      scale: `-= ${this.roadDetails.laneVehicleScale}`,
      duration: 250,
      ease: Phaser.Math.Easing.Linear
    });

    this.PlayLaneChangeSound();

    return true;
  }

  public changeLaneDown(): boolean {
    if (
      this.currentLane >= this.roadDetails.laneCount - 1 ||
      this.rightLaneCount > 0 ||
      (this.prevMoveSpeedX < this.driverProfile.minLaneChangeSpeed && !this.isStuckInTraffic && !this.isBlockedByObstacles) ||
      this.isLocked
    )
      return false;
    if (this.isChangingLane) {
      // if (!this.IsPlayer) {
      //   this.queueLaneChangeDown++;
      // }
      return false;
    }

    this.isChangingLane = true;
    this.hasChangedLaneOnce = true;

    this.currentLane++;
    this.setDepth(this.currentLane);

    console.log(`${this.CurrentSkin}: Down to lane ${this.currentLane}`);

    this.scene.add.tween({
      targets: this,
      // y: `+= ${LevelUtil.getLaneYFromTopToTop(this.currentLane, this.roadDetails)}`,
      y: `+= ${LevelUtil.getLaneYChangeMidToMid(this.currentLane - 1, this.currentLane, this.roadDetails)}`,
      scale: `+= ${this.roadDetails.laneVehicleScale}`,
      duration: 250,
      ease: Phaser.Math.Easing.Linear,
      onComplete: () => {
        this.isChangingLane = false;
        this.postLaneChange();
      },
      onCompleteScope: this
    });

    this.scene.add.tween({
      targets: this.taxiUI,
      scale: `+= ${this.roadDetails.laneVehicleScale}`,
      duration: 250,
      ease: Phaser.Math.Easing.Linear
    });

    this.PlayLaneChangeSound();

    return true;
  }

  private PlayLaneChangeSound(){
    let rand = Phaser.Math.Between(0, 4) + 1;
    if (rand <4) {
      this.playPlayerSound('SndLaneChange'+rand);
    }
  }

  public randomLaneChange() {
    if (this.IsChangingLane) this.queuedRandomLaneChange = true;
    else {
      let rand = Phaser.Math.Between(0, 1);
      if (rand == 0) {
        if (!this.changeLaneUp()) {
          this.changeLaneDown();
        }
      } else {
        if (!this.changeLaneDown()) {
          this.changeLaneUp();
        }
      }
    }
  }

  public boost(boostSpeed: number) {
    if (this.IsTaxi && !this.isStuckInTraffic && !this.isBlockedByObstacles) {
      this.boostSpeed = Math.min(this.topBoostSpeedPerMS, this.boostSpeed + boostSpeed);
      this.character.boost();
      this.setEngineSFX('SndEngine4');
      app.audioHandler.playSound(this.scene, 'SndPickup', 'sfx');
      // if (this.vehicleDetails.profile.driver == Driver.PlayerTaxi) console.log('BoostSpeed', this.boostSpeed, this.topBoostSpeed);
    }
  }

  public slow(slowStrength: number, hitEffect: HitEffectValues) {
    if (this.driverProfile.driver == Driver.PlayerTaxi || this.driverProfile.driver == Driver.NPCTaxi) {
      this.boostSpeed = 0;

      if (hitEffect == HitEffect.Impact) {
        this.character.bounce();
        this.drag += 10 * slowStrength;
      } else if (hitEffect == HitEffect.Slide) {
        this.character.slide();
        app.audioHandler.playSound(this.scene, 'SndObstacleSkid', 'sfx');
        this.randomLaneChange();
      }
    }
  }

  public addVehicleBehind() {
    this.backCount++;

    if (this.backCount > 0) this.isBlockingTraffic = true;
  }

  public removeVehicleBehind() {
    this.backCount--;

    if (this.backCount <= 0) this.isBlockingTraffic = false;
  }

  public pickupPassenger(passenger: Passenger) {
    if(this.driverProfile.driver == Driver.PlayerTaxi)
    {
      console.log('Picked up passenger!',this.driverProfile.driver, this.currentTime);
      passenger.pickedUp();
      // this.taxiUI?.showScore(passenger.PickupBonus);
      this.addScore(passenger.PickupBonus);
      this.taxiUI?.addPassenger(passenger, false);
      this.passengers.push({
        passenger: passenger,
        rideStartX: Math.trunc(this.x),
        lastScoredX: Math.trunc(this.x),
        time: this.currentTime,
        farePointsAccrued: 0,
        unstealable: this.passengerStealDefence
      });
      app.audioHandler.playSound(this.scene, 'SndPickup2', 'sfx');
    }
  }

  public dropOffPassenger(passenger: Passenger): {success:boolean, bonusAmount:number} {
    console.log('Drop off passenger!', this.currentTime);
    if(this.driverProfile.driver == Driver.PlayerTaxi)
    {
      for (let i = this.passengers.length - 1; i >= 0; i--) {
        let taxiPassenger = this.passengers[i];

        if (taxiPassenger.passenger == passenger) {
          // this.taxiUI?.showScore(passenger.DropOffBonus);
          let distanceToScore = this.x - taxiPassenger.rideStartX;
          let time = (this.currentTime-taxiPassenger.time)/1000;
          let targetTime = (distanceToScore / this.topSpeedPerMS)/1000;
          console.log("dist:: " + distanceToScore, time, this.topSpeedPerMS);
          let bonus = Math.round( passenger.DropOffBonus*(targetTime/time) );
          if(bonus < 10)
          {
            bonus = 10;
          }
          this.addScore(bonus);
          this.taxiUI?.removePassenger(passenger);
          this.passengers.splice(i, 1);
          app.audioHandler.playSound(this.scene, 'SndPickup2', 'sfx');
          return {success:true, bonusAmount: bonus};
        }
      }
    }

    return {success:false, bonusAmount: 0};
  }

  public findPassengerToSteal(): PickedUpPassenger | undefined {
    // if (this.passengers.length <= 0) return;
    // for (let i = this.passengers.length - 1; i >= 0; i--) {
    //   const pickedUpPassenger = this.passengers[i];
    //   if (pickedUpPassenger.unstealable <= 0) {
    //     return pickedUpPassenger;
    //   }
    // }
    return;
  }

  public startStealingPassenger(targetVehicle: Vehicle) {
    //this.vehicleManager.StealManager.startStealing(this, targetVehicle);
  }

  // public changeStealingPassenger(oldPassenger: Passenger, newPassenger: Passenger) {
  //   for (const passengerSteal of this.ongoingPassengerSteals) {
  //     if (passengerSteal.passenger == oldPassenger) {
  //       // Update UI.
  //       passengerSteal.passenger = newPassenger;
  //       passengerSteal.isInContact = false;
  //     }
  //   }
  // }

  public stopStealingPassenger(targetVehicle: Vehicle) {
    // this.vehicleManager.StealManager.stopStealing(this, targetVehicle);
  }

  public startBeingStolen(stealingVehicle: Vehicle, passenger: Passenger) {
    // this.stealUI?.showBeingStolen();
  }

  public updateBeingStolen(stealingVehicle: Vehicle, stealProgress: number, passenger: Passenger) {
    // this.taxiUI!.updateBeingStolen(stealingVehicle, stealProgress, passenger);
    // this.stealUI?.updateBeingStolen(stealProgress / 100);
  }

  public stopBeingStolen(passenger: Passenger) {
    // this.taxiUI?.stopBeingStolen(passenger);
    // this.stealUI?.hideBeingStolen();
  }

  public stealPassengerFrom(targetVehicle: Vehicle, passenger: Passenger) {
    console.log('Passenger Stolen!!');
    // app.audioHandler.playSound(this.scene, 'SndSteal2', 'sfx');
    // targetVehicle.loosePassengerTo(this, passenger);
    // // targetVehicle.stopBeingStolen(this, passenger);
    // targetVehicle.stopBeingStolen(passenger);
    // this.taxiUI?.updatePassenger(passenger, 1);
    // this.stealUI?.hideStealingFrom();
    // this.showOutOfRangeOfStealingWithTarget(targetVehicle);
  }

  public loosePassengerTo(otherTaxi: Vehicle, passengerToGet: Passenger) {
    // let pickedUpPassenger = this.passengers.pop();
    // if (!pickedUpPassenger) return;

    // this.taxiUI?.removePassenger(pickedUpPassenger.passenger);
    // otherTaxi.gainPassengerFrom(pickedUpPassenger.passenger, this);
  }

  public gainPassengerFrom(passenger: Passenger, otherTaxi: Vehicle) {
    // console.log('Gained passenger!');
    // // this.taxiUI?.addPassenger(passenger);
    // this.passengers.push({
    //   passenger: passenger,
    //   rideStartX: Math.trunc(this.x),
    //   lastScoredX: Math.trunc(this.x),
    //   farePointsAccrued: 0,
    //   unstealable: this.passengerStealDefence
    // });
  }

  public showInRangeOfStealing(vehicle: Vehicle) {
    // if (!this.inRangeOfStealing.has(vehicle)) {
    //   // vehicle.showInRangeOfBeingStolen();
    //   this.inRangeOfStealing.add(vehicle);
    // }
  }

  public showOutOfRangeOfStealing(vehicle: Vehicle) {
    // if (this.inRangeOfStealing.has(vehicle)) {
    //   // vehicle.showOutRangeOfBeingStolen();
    //   this.inRangeOfStealing.delete(vehicle);
    // }
  }

  public showInRangeOfStealingWithTarget(vehicle: Vehicle) {
    // if (!this.inRangeOfStealingWithTarget.has(vehicle)) {
    //   vehicle.showInRangeOfBeingStolen();
    //   this.inRangeOfStealingWithTarget.add(vehicle);
    // }
  }

  public showOutOfRangeOfStealingWithTarget(vehicle: Vehicle) {
    // if (this.inRangeOfStealingWithTarget.has(vehicle)) {
    //   vehicle.showOutRangeOfBeingStolen();
    //   this.inRangeOfStealingWithTarget.delete(vehicle);
    // }
  }

  public showInRangeOfBeingStolen() {
    // this.inRangeOfBeingStolen++;
  }

  public showOutRangeOfBeingStolen() {
    // this.inRangeOfBeingStolen--;
  }

  public update(time: number, delta: number) {
    // If we're off the map we can skip updating.
    if (this.isOffScreen) return;

    // If we're a safe way past the edge of the map then we've finished the run.
    if (this.x > this.vehicleDetails.endPointX + this.safeExtraFinishDistance) {
      this.isOffScreen = true;
      this.cleanUp();
      return;
    }

    this.currentTime += delta;

    for (let i = 0; i < this.passengers.length; i++) {
      const pickedUpPassenger = this.passengers[i];
      if (pickedUpPassenger.unstealable > 0) {
        pickedUpPassenger.unstealable -= delta;
        if (pickedUpPassenger.unstealable < 0) {
          pickedUpPassenger.unstealable = 0;
        }
      }

      let distanceToScore = Math.trunc(this.x) - pickedUpPassenger.lastScoredX;
      if (distanceToScore > 0) {
        let pointsToScore = distanceToScore * pickedUpPassenger.passenger.DistanceRate;
        pickedUpPassenger.lastScoredX = Math.trunc(this.x);
        pickedUpPassenger.farePointsAccrued += pointsToScore;

        this.addScore(pointsToScore);
        this.addDistance(distanceToScore);
      }
    }

    if (!this.isInRangeWithTarget && (this.inRangeOfStealingWithTarget.size > 0 || this.inRangeOfBeingStolen > 0)) {
      // this.stealUI?.showInRange(true);
      this.isInRangeWithTarget = true;
    } else if (!this.isInRange && !this.isInRangeWithTarget && this.inRangeOfStealing.size > 0) {
      // this.stealUI?.showInRange(false);
      this.isInRange = true;
    } else if (this.isInRangeWithTarget && this.inRangeOfStealingWithTarget.size <= 0 && this.inRangeOfBeingStolen <= 0) {
      // this.stealUI?.showOutOfRange();
      this.isInRangeWithTarget = false;
    } else if (this.isInRange && this.inRangeOfStealing.size <= 0) {
      // this.stealUI?.showOutOfRange();
      this.isInRange = false;
    }

    // Civilian behaviour.
    if (this.IsTraffic && (this.isBlockingTraffic || this.isBlockedByObstacles) && !this.IsChangingLane) {
      this.randomLaneChange();
    }
  }

  public physicsUpdate(time: number, delta: number) {
    // If we're off the map we can skip updating.
    if (this.isOffScreen) return;

    if (this.boostSpeed > 0) {
      this.boostSpeed = Math.max(0, this.boostSpeed - this.boostSpeedDecayRatePerMS);
      if (this.boostSpeed <= 0) {
        this.setEngineSFX('SndEngine1');
        this.boostSpeed = 0;
      }
    } else if (this.boostSpeed < 0) {
      this.boostSpeed = 0;

      // if (this.vehicleDetails.profile.driver == Driver.PlayerTaxi) console.log('Boost Over!', this.boostSpeed, this.topBoostSpeed);
    }

    if (this.isMoving) {
      let calculatedTopSpeed = this.topSpeedPerMS * delta;
      let calculatedMaxSpeed = this.maxSpeedPerMS * delta;
      let calculatedAcceleration = this.isAccelerating ? this.accelerationPerMS : -this.decelerationPerMS;

      // If NPC change speed based on distance from player.
      if (this.IsNPC) {
        let playerPosition = this.vehicleManager.PlayerPosition;
        if (playerPosition) {
          let distanceToPlayer = playerPosition.x - this.x;
          // Ahead.
          if (distanceToPlayer <= -this.driverProfile.playerMinDistanceAhead) {
            let distanceMultiplier = this.calculatedNPCBoost(
              distanceToPlayer,
              this.driverProfile.playerDistanceMultiplierAhead,
              this.driverProfile.playerMinDistanceAhead,
              this.driverProfile.playerMaxDistanceAhead
            );

            let changeInTopSpeed = calculatedTopSpeed * distanceMultiplier;
            calculatedTopSpeed -= changeInTopSpeed;
            calculatedMaxSpeed -= changeInTopSpeed;

            // Behind.
          } else if (distanceToPlayer >= this.driverProfile.playerMinDistanceBehind) {
            let distanceMultiplier = this.calculatedNPCBoost(
              distanceToPlayer,
              this.driverProfile.playerDistanceMultiplierBehind,
              this.driverProfile.playerMinDistanceBehind,
              this.driverProfile.playerMaxDistanceBehind
            );

            let changeInTopSpeed = calculatedTopSpeed * distanceMultiplier;
            calculatedTopSpeed += changeInTopSpeed;
            calculatedMaxSpeed += changeInTopSpeed;
          }
        }
      }

      this.normalSpeed = Phaser.Math.Clamp(this.normalSpeed + calculatedAcceleration * delta - this.drag, 0, calculatedTopSpeed);
      this.drag = 0;

      if ((this.isStuckInTraffic || this.isBlockedByObstacles) && this.normalSpeed > this.speedLimitPerMS * delta)
        this.normalSpeed = this.speedLimitPerMS * delta;

      let moveSpeedX = Math.min(
        Math.min(this.normalSpeed + this.boostSpeed * delta, calculatedMaxSpeed),
        (this.isStuckInTraffic || this.isBlockedByObstacles ? this.speedLimitPerMS : calculatedMaxSpeed) * delta
      );
      let moveSpeedY = this.body.velocity.y;

      if (moveSpeedX < this.minSpeedPerMS) {
        moveSpeedX = 0;
      }

      let vol: number = moveSpeedX / calculatedMaxSpeed;
      if (this.IsPlayer) {
        app.audioHandler.setVolume(this.sndEngineRef, 'sfx', vol);
      }

      // this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: moveSpeedX, y: moveSpeedY });
      this.scene.matter.body.setPosition(this.body as MatterJS.BodyType, { x: this.x + moveSpeedX, y: this.y + moveSpeedY });
      // console.log(moveSpeedX);
      this.prevMoveSpeedX = moveSpeedX;
    }

    // Move the ui with us.
    if (this.taxiUI) {
      this.taxiUI.setPosition(this.x, this.y);
      //if(!this.IsPlayer){
      //  this.taxiUI.iconDisplay();
      //}
    }

    // if (this.stealUI) {
    //   this.stealUI.update(time, delta, this);
    // }
  }

  protected setEngineSFX(sndName: string) {
    this.stopEngineSFX();
    if (this.IsPlayer) {
      if (this.sndEngineRef == '') {
        let success;
        [this.sndEngineRef, success] = app.audioHandler.playSound(this.scene, sndName, 'sfx', {
          volume: 0.0,
          loop: true
        });
        console.log('SFX ', this.sndEngineRef);
      }
    }
  }

  protected stopEngineSFX() {
    if (this.sndEngineRef != '') {
      app.audioHandler.stopAndRemoveSound(this.sndEngineRef, 'sfx');
      this.sndEngineRef = '';
    }
  }

  protected playPlayerSound(snd: string, vol: number = 1.0) {
    if (this.IsPlayer) {
      app.audioHandler.playSound(this.scene, snd, 'sfx', {
        volume: vol
      });
    }
  }

  protected addScore(score: number) {
    this.pointsScored += score;

    if (this.IsPlayer) this.scene.registry.inc(GameDataHandler.keys.Score, score);
  }

  protected addDistance(distance: number) {
    if (this.IsPlayer) this.scene.registry.inc(GameDataHandler.keys.Distance, distance);
  }

  protected calculatedNPCBoost(distanceToPlayer: number, multiplier: number, minDistance: number, maxDistance: number): number {
    let range = maxDistance - minDistance;
    let distance = Math.abs(distanceToPlayer) - minDistance;
    let distancePercent = distance / range;
    let changeInTopSpeed = distancePercent * multiplier;

    return changeInTopSpeed;
  }

  protected postLaneChange() {
    if (this.queuedRandomLaneChange) {
      this.queuedRandomLaneChange = false;
      this.randomLaneChange();
      return;
    }

    if (this.IsPlayer) {
      if (this.isLaneChangingUp) {
        this.changeLaneUp();
      } else if (this.isLaneChangingDown) {
        this.changeLaneDown();
      }
    } else {
      // let diff = this.queueLaneChangeUp - this.queueLaneChangeDown;
      // this.queueLaneChangeUp = 0;
      // this.queueLaneChangeDown = 0;
      // if (diff > 0) this.changeLaneUp();
      // else if (diff < 0) this.changeLaneDown();
    }
  }

  protected injectMatter(target: Phaser.GameObjects.GameObject) {
    let x = this.x;
    let y = this.y;

    let matterSprite = this.scene.matter.add.gameObject(target) as Phaser.Physics.Matter.Sprite;

    let offsetX = 0;
    let offsetY = this.character.displayHeight - this.roadDetails.laneHeight - 10;
    let width: number = Vehicle.skinsToSize.get(this.CurrentSkin) ?? this.character.displayWidth;
    let height = this.roadDetails.laneHeight;
    let shrinkY = 30;
    let leftOffset = 10;
    let leftShrink = 40;
    let rightOffset = 0;
    let rightShrink = 40;
    let stealBorderOffsetX = 140;
    let stealBorderWidth = 140;

    this.vehicleBody = this.scene.matter.bodies.rectangle(offsetX, offsetY, width, height - shrinkY, {
      label: BodyTypes.Vehicle,
      onCollideCallback: this.onOwnVehicleCollisionStart.bind(this),
      isSensor: true
    });

    let frontSensor = this.scene.matter.bodies.rectangle(offsetX + width * 0.5 + this.breakDistance * 0.5, offsetY, this.breakDistance, 50, {
      label: BodyTypes.VehicleSensor,
      onCollideCallback: this.onFrontCollisionStart.bind(this),
      onCollideActiveCallback: this.onFrontCollisionActive.bind(this),
      onCollideEndCallback: this.onFrontCollisionEnd.bind(this),
      isSensor: true
    });

    let leftSensor = this.scene.matter.bodies.rectangle(offsetX, offsetY - height + leftOffset, width + this.vehicleGap, height - leftShrink, {
      label: BodyTypes.VehicleSensor,
      onCollideCallback: this.onLeftCollisionStart.bind(this),
      onCollideEndCallback: this.onLeftCollisionEnd.bind(this),
      isSensor: true
    });

    let rightSensor = this.scene.matter.bodies.rectangle(offsetX, offsetY + height + rightOffset, width + this.vehicleGap, height - rightShrink, {
      label: BodyTypes.VehicleSensor,
      onCollideCallback: this.onRightCollisionStart.bind(this),
      onCollideEndCallback: this.onRightCollisionEnd.bind(this),
      isSensor: true
    });

    let stealSensor = this.scene.matter.bodies.rectangle(
      0 - stealBorderWidth + stealBorderOffsetX,
      offsetY + height * 0,
      width + 3 * stealBorderWidth,
      height * 1.8 * 2,
      this.IsTaxi
        ? {
            label: BodyTypes.VehicleSensor,
            onCollideCallback: this.onStealCollisionStart.bind(this),
            onCollideActiveCallback: this.onStealCollisionActive.bind(this),
            onCollideEndCallback: this.onStealCollisionEnd.bind(this),
            isSensor: true
          }
        : {
            label: BodyTypes.VehicleSensor,
            isSensor: true
          }
    );

    let compoundBody = this.scene.matter.body.create({
      label: BodyTypes.Vehicle,
      parts: [this.vehicleBody, frontSensor, leftSensor, rightSensor, stealSensor],
      restitution: 0,
      inertia: Infinity
    });

    compoundBody.position.x -= 20;
    compoundBody.position.y = this.character.displayOriginY * this.character.scaleY * 0.5 - 30;
    if (this.CurrentSkin == VehicleSkins.CarGreen) compoundBody.position.y -= 15;

    matterSprite.setExistingBody(compoundBody);

    this.scene.matter.body.setPosition(this.body as MatterJS.BodyType, this.scene.matter.vector.create(x, y));
    this.scene.matter.setVelocity(this.body as MatterJS.BodyType, 0, 0);
  }

  protected onOwnVehicleCollisionStart(pair: MatterJS.ICollisionPair) {
    if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Boost) {
      this.onBoostCollisionStart(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Boost) {
      this.onBoostCollisionStart(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }

    if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Obstacle) {
      this.onObstacleCollisionStart(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Obstacle) {
      this.onObstacleCollisionStart(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }
  }

  protected onFrontCollisionStart(pair: MatterJS.ICollisionPair) {
    if (
      ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.VehicleSensor && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle) ||
      ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.VehicleSensor)
    ) {
      this.frontVehicleCount++;

      if (this.frontVehicleCount > 0) this.isStuckInTraffic = true;

      if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).gameObject instanceof Vehicle) {
        ((pair.bodyB as MatterJS.BodyType).gameObject as Vehicle).addVehicleBehind();
      } else if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).gameObject instanceof Vehicle) {
        ((pair.bodyA as MatterJS.BodyType).gameObject as Vehicle).addVehicleBehind();
      }
    }

    if (
      ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.VehicleSensor &&
        (pair.bodyA as MatterJS.BodyType).gameObject instanceof Vehicle &&
        ((pair.bodyA as MatterJS.BodyType).gameObject as Vehicle).IsTraffic &&
        (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Obstacle &&
        (pair.bodyB as MatterJS.BodyType).gameObject instanceof ObstacleBoxes) ||
      ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.VehicleSensor &&
        (pair.bodyB as MatterJS.BodyType).gameObject instanceof Vehicle &&
        ((pair.bodyB as MatterJS.BodyType).gameObject as Vehicle).IsTraffic &&
        (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Obstacle &&
        (pair.bodyA as MatterJS.BodyType).gameObject instanceof ObstacleBoxes)
    ) {
      this.frontObstacleCount++;

      if (this.frontObstacleCount > 0) this.isBlockedByObstacles = true;
    }
  }

  protected onFrontCollisionActive(pair: MatterJS.ICollisionPair) {
    if (
      ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.VehicleSensor && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle) ||
      ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.VehicleSensor)
    ) {
      if ((pair.bodyA as MatterJS.BodyType).position.x <= (pair.bodyB as MatterJS.BodyType).position.x) {
        this.onFrontBlockerCollisionActive(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
      } else if ((pair.bodyB as MatterJS.BodyType).position.x < (pair.bodyA as MatterJS.BodyType).position.x) {
        this.onFrontBlockerCollisionActive(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
      }
    }

    if (
      (pair.bodyA as MatterJS.BodyType).label == BodyTypes.VehicleSensor &&
      (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Obstacle &&
      (pair.bodyB as MatterJS.BodyType).gameObject instanceof ObstacleBoxes
    ) {
      this.onFrontBlockerCollisionActive(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if (
      (pair.bodyB as MatterJS.BodyType).label == BodyTypes.VehicleSensor &&
      (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Obstacle &&
      (pair.bodyA as MatterJS.BodyType).gameObject instanceof ObstacleBoxes
    ) {
      this.onFrontBlockerCollisionActive(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }
  }

  protected onFrontCollisionEnd(pair: MatterJS.ICollisionPair) {
    if (
      ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.VehicleSensor && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle) ||
      ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.VehicleSensor)
    ) {
      this.frontVehicleCount--;

      if (this.frontVehicleCount <= 0) {
        this.speedLimitPerMS = this.driverProfile.maxSpeedPerMS;
        this.isStuckInTraffic = false;
      }

      if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).gameObject instanceof Vehicle) {
        ((pair.bodyB as MatterJS.BodyType).gameObject as Vehicle).removeVehicleBehind();
      } else if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).gameObject) {
        ((pair.bodyA as MatterJS.BodyType).gameObject as Vehicle).removeVehicleBehind();
      }
    }

    if (
      ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.VehicleSensor &&
        (pair.bodyA as MatterJS.BodyType).gameObject instanceof Vehicle &&
        ((pair.bodyA as MatterJS.BodyType).gameObject as Vehicle).IsTraffic &&
        (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Obstacle &&
        (pair.bodyB as MatterJS.BodyType).gameObject instanceof ObstacleBoxes) ||
      ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.VehicleSensor &&
        (pair.bodyB as MatterJS.BodyType).gameObject instanceof Vehicle &&
        ((pair.bodyB as MatterJS.BodyType).gameObject as Vehicle).IsTraffic &&
        (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Obstacle &&
        (pair.bodyA as MatterJS.BodyType).gameObject instanceof ObstacleBoxes)
    ) {
      this.frontObstacleCount--;

      if (this.frontObstacleCount <= 0) {
        this.speedLimitPerMS = this.driverProfile.maxSpeedPerMS;
        this.isBlockedByObstacles = false;
      }
    }
  }

  protected onLeftCollisionStart(pair: MatterJS.ICollisionPair) {
    if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.leftLaneCount++;
      // this.onVehicleSideStealStart(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.leftLaneCount++;
      // this.onVehicleSideStealStart(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }
  }

  protected onLeftCollisionEnd(pair: MatterJS.ICollisionPair) {
    if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.leftLaneCount--;
      // this.onVehicleSideStealEnd(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.leftLaneCount--;
      // this.onVehicleSideStealEnd(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }
  }

  protected onRightCollisionStart(pair: MatterJS.ICollisionPair) {
    if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.rightLaneCount++;
      // this.onVehicleSideStealStart(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.rightLaneCount++;
      // this.onVehicleSideStealStart(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }
  }

  protected onRightCollisionEnd(pair: MatterJS.ICollisionPair) {
    if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.rightLaneCount--;
      // this.onVehicleSideStealEnd(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.rightLaneCount--;
      // this.onVehicleSideStealEnd(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }
  }

  protected onStealCollisionStart(pair: MatterJS.ICollisionPair) {
    if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.onStealInRange(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.onStealInRange(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }
  }

  protected onStealCollisionActive(pair: MatterJS.ICollisionPair) {
    if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.onStealActive(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.onStealActive(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }
  }

  protected onStealCollisionEnd(pair: MatterJS.ICollisionPair) {
    if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.onStealOutOfRange(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).label == BodyTypes.VehicleSensor) {
      this.onStealOutOfRange(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }
  }

  protected onBoostCollisionStart(pair: MatterJS.ICollisionPair, vehicle: MatterJS.BodyType, boostEntity: MatterJS.BodyType) {
    if (vehicle.gameObject instanceof Vehicle && boostEntity.gameObject instanceof BoostPadAnimated) {
      vehicle.gameObject.boost(boostEntity.gameObject.boostSpeed);
      this.currentTime -= 150*(this.prevMoveSpeedX/this.maxSpeedPerMS);
    }
  }

  protected onObstacleCollisionStart(pair: MatterJS.ICollisionPair, vehicle: MatterJS.BodyType, obstacleEntity: MatterJS.BodyType) {
    if (vehicle.gameObject instanceof Vehicle && vehicle.gameObject.IsTaxi && obstacleEntity.gameObject instanceof Obstacle) {
      vehicle.gameObject.slow(obstacleEntity.gameObject.slowStrength, obstacleEntity.gameObject.hitEffect);
      obstacleEntity.gameObject.onHit(vehicle.gameObject.IsPlayer, true, vehicle.bounds.max.x, 500);
      this.currentTime += 200*(this.prevMoveSpeedX/this.maxSpeedPerMS);
    }
  }

  protected onFrontBlockerCollisionActive(pair: MatterJS.ICollisionPair, vehicleSensor: MatterJS.BodyType, frontBlocker: MatterJS.BodyType) {
    let distance = this.breakDistance - this.vehicleGap + frontBlocker.bounds.min.x - vehicleSensor.bounds.max.x;
    this.speedLimitPerMS = Phaser.Math.Clamp(distance / (this.breakDistance - this.vehicleGap), 0, 1) * this.driverProfile.maxSpeedPerMS;
    this.boostSpeed = 0;
  }

  protected checkIfVehicleInStealArea(stealRadius: Phaser.Geom.Circle, vehicleBounds: Phaser.Geom.Rectangle) {
    // this.debugGraphics.clear();
    // this.debugGraphics.lineStyle(3, 0x00ff00, 1);
    // this.debugGraphics.strokeCircle(stealRadius.x, stealRadius.y, stealRadius.radius);
    // // this.debugGraphics.lineStyle(3, 0xff0000, 1);
    // // this.debugGraphics.strokeRect(this.vehicleBounds.x, this.vehicleBounds.y, this.vehicleBounds.width, this.vehicleBounds.height);
    // this.debugGraphics.lineStyle(3, 0x0000ff, 1);
    // this.debugGraphics.strokeRect(vehicleBounds.x, vehicleBounds.y, vehicleBounds.width, vehicleBounds.height);

    // return Phaser.Geom.Intersects.CircleToRectangle(stealRadius, vehicleBounds);
    return false;

    // let distX = Math.abs(this.x - vehicleBounds.centerX);
    // let distY = Math.abs(this.y - vehicleBounds.centerY);

    // if (distX > vehicleBounds.width / 2 + stealRadius.radius) return false;
    // if (distY > vehicleBounds.height / 2 + stealRadius.radius) return false;

    // if (distX <= vehicleBounds.width / 2) return true;
    // if (distY <= vehicleBounds.height / 2) return true;

    // let cornerDist = Math.pow(distX - vehicleBounds.width / 2, 2) + Math.pow(distY - vehicleBounds.height / 2, 2);
    // return cornerDist <= Math.pow(stealRadius.radius, 2);
  }

  protected onStealInRange(pair: MatterJS.ICollisionPair, vehicle: MatterJS.BodyType, ourVehicleSensor: MatterJS.BodyType) {
    // if (
    //   vehicle.gameObject instanceof Vehicle &&
    //   vehicle.gameObject.IsTaxi &&
    //   vehicle.gameObject.HasPassengers &&
    //   ourVehicleSensor.gameObject instanceof Vehicle &&
    //   ourVehicleSensor.gameObject.IsTaxi
    // ) {
    //   let rect = new Phaser.Geom.Rectangle(vehicle.bounds.min.x, vehicle.bounds.min.y, vehicle.bounds.max.x - vehicle.bounds.min.x, vehicle.bounds.max.y - vehicle.bounds.min.y);
    //   if (this.checkIfVehicleInStealArea(rect)) {
    //     ourVehicleSensor.gameObject.startStealingPassenger(vehicle.gameObject);
    //     ourVehicleSensor.gameObject.showInRangeOfStealing(vehicle.gameObject);
    //   }
    // }
  }

  protected onStealActive(pair: MatterJS.ICollisionPair, vehicle: MatterJS.BodyType, ourVehicleSensor: MatterJS.BodyType) {
    if (
      vehicle.gameObject instanceof Vehicle &&
      vehicle.gameObject.IsTaxi &&
      ourVehicleSensor.gameObject instanceof Vehicle &&
      ourVehicleSensor.gameObject.IsTaxi
    ) {
      // this.refreshStealGeometry(vehicle.gameObject, vehicle);

      if (this.checkIfVehicleInStealArea(ourVehicleSensor.gameObject.stealRadius, vehicle.gameObject.VehicleBounds)) {
        ourVehicleSensor.gameObject.showInRangeOfStealing(vehicle.gameObject);

        if (vehicle.gameObject.HasPassengers) {
          ourVehicleSensor.gameObject.startStealingPassenger(vehicle.gameObject);
          ourVehicleSensor.gameObject.showInRangeOfStealingWithTarget(vehicle.gameObject);
        }
      } else {
        ourVehicleSensor.gameObject.stopStealingPassenger(vehicle.gameObject);
        ourVehicleSensor.gameObject.showOutOfRangeOfStealing(vehicle.gameObject);
        ourVehicleSensor.gameObject.showOutOfRangeOfStealingWithTarget(vehicle.gameObject);
      }
    }
  }

  protected onStealOutOfRange(pair: MatterJS.ICollisionPair, vehicle: MatterJS.BodyType, ourVehicleSensor: MatterJS.BodyType) {
    if (
      vehicle.gameObject instanceof Vehicle &&
      vehicle.gameObject.IsTaxi &&
      ourVehicleSensor.gameObject instanceof Vehicle &&
      ourVehicleSensor.gameObject.IsTaxi
    ) {
      ourVehicleSensor.gameObject.stopStealingPassenger(vehicle.gameObject);
      ourVehicleSensor.gameObject.showOutOfRangeOfStealing(vehicle.gameObject);
      ourVehicleSensor.gameObject.showOutOfRangeOfStealingWithTarget(vehicle.gameObject);
    }
  }
}
